import React, { useEffect, useState } from 'react';
import { Download, ChevronDown, ChevronUp, Check, X, AlertTriangle, ThumbsUp, Users, BookOpen, Search, Star, StarHalf, RefreshCw } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { intentionsService } from '../../services/intentionsService';
import { gridsService } from '../../services/gridsService';
import { staffService } from '../../services/staffService';
import { supabase } from '../../supabase/config';
import { useLearningAreas } from '../../hooks/useLearningAreas';
import { displayLearningArea } from '../../constants/learningAreas';
interface TeacherPreference {
  id: string;
  teacherId: string;
  teacherName: string;
  department: string;
  preferenceType: 'subject' | 'year_level' | 'period' | 'room';
  preferenceValue: string;
  strength: 1 | 2 | 3 | 4 | 5; // 1 = lowest, 5 = highest
  notes: string;
}

interface TeacherAssignment {
  id: string;
  teacherId: string;
  teacherName: string;
  subjectCode: string;
  subjectName: string;
  yearLevel: string;
  periods: string[];
  room: string;
}

interface PreferenceSatisfactionData {
  overallSatisfaction: number;
  teacherSatisfaction: {
    teacherId: string;
    teacherName: string;
    department: string;
    satisfactionScore: number;
    preferencesCount: number;
    satisfiedCount: number;
    partialCount: number;
    unsatisfiedCount: number;
    details: {
      preferenceType: string;
      preferenceValue: string;
      strength: number;
      satisfied: boolean | 'partial';
      assignedValue: string;
    }[];
  }[];
  departmentSatisfaction: {
    department: string;
    satisfactionScore: number;
    teacherCount: number;
    preferencesCount: number;
    satisfiedCount: number;
    partialCount: number;
    unsatisfiedCount: number;
  }[];
  preferenceTypeSatisfaction: {
    preferenceType: string;
    satisfactionScore: number;
    preferencesCount: number;
    satisfiedCount: number;
    partialCount: number;
    unsatisfiedCount: number;
  }[];
  yearLevelSatisfaction: {
    yearLevel: string;
    satisfactionScore: number;
    preferencesCount: number;
    satisfiedCount: number;
    partialCount: number;
    unsatisfiedCount: number;
  }[];
}

const PreferenceSatisfaction: React.FC = () => {
  const { addToast } = useToast();
  const { learningAreaNames } = useLearningAreas();
  const [preferences, setPreferences] = useState<TeacherPreference[]>([]);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [satisfactionData, setSatisfactionData] = useState<PreferenceSatisfactionData | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<string | null>(null);
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterSatisfaction, setFilterSatisfaction] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Discover which academic years actually have intentions, and default the
  // selector to the most recent one (not a hardcoded future year that may hold
  // no data). Runs once on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('teaching_intentions')
          .select('year');
        if (error) throw error;
        const years = Array.from(new Set((data || []).map((r: { year: number }) => r.year)))
          .filter((y): y is number => typeof y === 'number')
          .sort((a, b) => b - a);
        setAvailableYears(years);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error loading available intention years:', err);
        addToast('Could not load the list of years with intentions', 'error');
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load real data whenever the selected year changes.
  useEffect(() => {
    if (selectedYear != null) loadRealData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const loadRealData = async () => {
    if (selectedYear == null) return;
    setIsLoading(true);
    try {
      // Preferences come from teaching intentions; allocations from every active
      // grid (a teacher may be timetabled across Main / Oratory / RUAH / VET).
      const [intentions, grids, staffMembers] = await Promise.all([
        intentionsService.getIntentionsByYear(selectedYear),
        gridsService.getAllGrids(),
        staffService.getActiveStaff()
      ]);

      // Grid lineAssignments and intention.staffId both use a short staff CODE
      // (e.g. "ATS"), NOT the staff-row UUID — so resolve display names via the
      // code fields and fall back to the code itself.
      const staffNameByCode = new Map<string, string>();
      staffMembers.forEach(s => {
        [s.staff_code, s.initial, s.staff_id].forEach(code => {
          if (code) staffNameByCode.set(String(code), s.name || String(code));
        });
      });

      const rankToStrength = (rank?: number): 1 | 2 | 3 | 4 | 5 =>
        Math.min(5, Math.max(1, 6 - (rank || 1))) as 1 | 2 | 3 | 4 | 5;

      // Newer submissions rank curriculum areas in preferences.curriculumAreas;
      // legacy imported rows use the teachingPreferences JSONB. Read whichever is
      // present so both shapes populate. Name/department come straight off the
      // intention, so rows whose code isn't in the staff table aren't dropped.
      const transformedPreferences: TeacherPreference[] = [];
      intentions.forEach(intention => {
        const teacherName = intention.staffName || 'Unknown';
        const department = intention.department || 'Unknown';

        const areas = (intention.preferences as any)?.curriculumAreas;
        if (Array.isArray(areas) && areas.length > 0) {
          areas.forEach((area: any, index: number) => {
            if (!area?.areaName) return;
            transformedPreferences.push({
              id: `${intention.staffId}-area-${index}`,
              teacherId: intention.staffId,
              teacherName,
              department,
              preferenceType: 'subject',
              preferenceValue: area.areaName,
              strength: rankToStrength(area.rank),
              notes: ''
            });
          });
          return;
        }

        // Legacy fallback: teachingPreferences[].learningArea
        if (Array.isArray(intention.teachingPreferences)) {
          intention.teachingPreferences.forEach((pref: any, index: number) => {
            if (!pref?.learningArea) return;
            transformedPreferences.push({
              id: `${intention.staffId}-subject-${index}`,
              teacherId: intention.staffId,
              teacherName,
              department,
              preferenceType: 'subject',
              preferenceValue: pref.learningArea,
              strength: rankToStrength(pref.rank),
              notes: pref.specificSubject ? `Specific: ${pref.specificSubject}` : ''
            });
          });
        }
      });

      // A lineAssignment value is a teacher code string, or a JSON array/object
      // for team-taught classes. Extract every code and key the assignment by it
      // so it matches intention.staffId directly.
      const parseCodes = (assigned: unknown): string[] => {
        if (typeof assigned !== 'string' || !assigned.trim()) return [];
        const t = assigned.trim();
        if (t.startsWith('[') || t.startsWith('{')) {
          try {
            const parsed = JSON.parse(t);
            if (Array.isArray(parsed)) return parsed.map((a: any) => a?.teacherCode || a).filter(Boolean);
            if (parsed?.teacherCode) return [parsed.teacherCode];
            return [];
          } catch {
            return [t];
          }
        }
        return [t];
      };

      const transformedAssignments: TeacherAssignment[] = [];
      grids.forEach(grid => {
        (grid.classes || []).forEach((classItem: any) => {
          const lineAssignments = classItem.lineAssignments || {};
          const codes = new Set<string>();
          Object.values(lineAssignments).forEach(v => parseCodes(v).forEach(c => codes.add(c)));
          codes.forEach(code => {
            transformedAssignments.push({
              id: `${grid.id}-${classItem.className}-${code}`,
              teacherId: code,
              teacherName: staffNameByCode.get(code) || code,
              subjectCode: classItem.curriculumArea || String(classItem.className) || '',
              subjectName: String(classItem.className) || '',
              yearLevel: classItem.yearLevel != null ? String(classItem.yearLevel) : '',
              periods: [], // Grid data doesn't carry period info
              room: '' // Grid data doesn't carry room info
            });
          });
        });
      });

      setPreferences(transformedPreferences);
      setAssignments(transformedAssignments);

      // With no stated preferences there is nothing to measure; the empty state
      // explains this. Assignments may legitimately be empty (nothing allocated
      // yet) — that surfaces honestly as unsatisfied preferences.
      if (transformedPreferences.length === 0) {
        setSatisfactionData(null);
      } else {
        calculateSatisfaction(transformedPreferences, transformedAssignments);
      }
    } catch (error) {
      console.error('Error loading preference satisfaction data:', error);
      addToast('Failed to load preference satisfaction data', 'error');
      setSatisfactionData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate satisfaction data (pure, synchronous)
  const calculateSatisfaction = (prefs: TeacherPreference[], assigns: TeacherAssignment[]) => {
      // Initialize data structure
      const data: PreferenceSatisfactionData = {
        overallSatisfaction: 0,
        teacherSatisfaction: [],
        departmentSatisfaction: [],
        preferenceTypeSatisfaction: [],
        yearLevelSatisfaction: []
      };

      // Calculate teacher satisfaction
      const teacherMap = new Map<string, {
        teacherId: string;
        teacherName: string;
        department: string;
        satisfactionScore: number;
        preferencesCount: number;
        satisfiedCount: number;
        partialCount: number;
        unsatisfiedCount: number;
        details: {
          preferenceType: string;
          preferenceValue: string;
          strength: number;
          satisfied: boolean | 'partial';
          assignedValue: string;
        }[];
      }>();

      // Process each preference
      prefs.forEach(pref => {
        // Get teacher data or initialize
        let teacher = teacherMap.get(pref.teacherId);
        if (!teacher) {
          teacher = {
            teacherId: pref.teacherId,
            teacherName: pref.teacherName,
            department: pref.department,
            satisfactionScore: 0,
            preferencesCount: 0,
            satisfiedCount: 0,
            partialCount: 0,
            unsatisfiedCount: 0,
            details: []
          };
          teacherMap.set(pref.teacherId, teacher);
        }

        // Find assignments for this teacher
        const teacherAssignments = assigns.filter(a => a.teacherId === pref.teacherId);

        // Check if preference is satisfied
        let satisfied: boolean | 'partial' = false;
        let assignedValue = 'None';

        if (pref.preferenceType === 'subject') {
          // Check if assigned subject matches preference (flexible matching)
          const subjectAssignment = teacherAssignments.find(a => 
            a.subjectCode?.toLowerCase().includes(pref.preferenceValue.toLowerCase()) ||
            pref.preferenceValue.toLowerCase().includes(a.subjectCode?.toLowerCase() || '')
          );
          if (subjectAssignment) {
            satisfied = true;
            assignedValue = subjectAssignment.subjectCode;
          } else {
            // List assigned subjects
            assignedValue = teacherAssignments.map(a => a.subjectCode).join(', ') || 'None';
          }
        } else if (pref.preferenceType === 'year_level') {
          // Check if the preference is for a range (e.g., "11-12")
          const yearLevels = pref.preferenceValue.split('-');
          const hasYearLevel = teacherAssignments.some(a => yearLevels.includes(a.yearLevel));
          
          if (yearLevels.length > 1) {
            // For ranges, check how many are satisfied
            const satisfiedYears = yearLevels.filter(yl => teacherAssignments.some(a => a.yearLevel === yl));
            if (satisfiedYears.length === yearLevels.length) {
              satisfied = true;
            } else if (satisfiedYears.length > 0) {
              satisfied = 'partial';
            }
            assignedValue = teacherAssignments.map(a => a.yearLevel).join(', ') || 'None';
          } else if (hasYearLevel) {
            satisfied = true;
            assignedValue = teacherAssignments.filter(a => yearLevels.includes(a.yearLevel)).map(a => a.yearLevel).join(', ');
          } else {
            assignedValue = teacherAssignments.map(a => a.yearLevel).join(', ') || 'None';
          }
        } else if (pref.preferenceType === 'period') {
          // For period preferences like "Morning", "Afternoon" (not available in grid data)
          satisfied = 'partial';
          assignedValue = 'Period data not available';
        } else if (pref.preferenceType === 'room') {
          // Room preferences (not available in grid data)
          satisfied = 'partial';
          assignedValue = 'Room data not available';
        }

        // Update teacher satisfaction data
        teacher.preferencesCount++;
        if (satisfied === true) {
          teacher.satisfiedCount++;
        } else if (satisfied === 'partial') {
          teacher.partialCount++;
        } else {
          teacher.unsatisfiedCount++;
        }

        // Add detail
        teacher.details.push({
          preferenceType: pref.preferenceType,
          preferenceValue: pref.preferenceValue,
          strength: pref.strength,
          satisfied,
          assignedValue
        });
      });

      // Calculate satisfaction scores for each teacher
      teacherMap.forEach(teacher => {
        let weightedScore = 0;
        let totalWeight = 0;

        teacher.details.forEach(detail => {
          const weight = detail.strength;
          totalWeight += weight;
          if (detail.satisfied === true) {
            weightedScore += weight * 1.0;
          } else if (detail.satisfied === 'partial') {
            weightedScore += weight * 0.5;
          }
        });

        teacher.satisfactionScore = totalWeight > 0 ? Math.round((weightedScore / totalWeight) * 100) : 0;
      });

      // Convert Map to array for teacher satisfaction
      data.teacherSatisfaction = Array.from(teacherMap.values());

      // Calculate department satisfaction
      const departmentMap = new Map<string, {
        department: string;
        satisfactionScore: number;
        teacherCount: number;
        preferencesCount: number;
        satisfiedCount: number;
        partialCount: number;
        unsatisfiedCount: number;
      }>();

      data.teacherSatisfaction.forEach(teacher => {
        let dept = departmentMap.get(teacher.department);
        if (!dept) {
          dept = {
            department: teacher.department,
            satisfactionScore: 0,
            teacherCount: 0,
            preferencesCount: 0,
            satisfiedCount: 0,
            partialCount: 0,
            unsatisfiedCount: 0
          };
          departmentMap.set(teacher.department, dept);
        }

        dept.teacherCount++;
        dept.preferencesCount += teacher.preferencesCount;
        dept.satisfiedCount += teacher.satisfiedCount;
        dept.partialCount += teacher.partialCount;
        dept.unsatisfiedCount += teacher.unsatisfiedCount;
      });

      // Calculate satisfaction scores for each department
      departmentMap.forEach(dept => {
        dept.satisfactionScore = dept.preferencesCount > 0 
          ? Math.round(((dept.satisfiedCount + dept.partialCount * 0.5) / dept.preferencesCount) * 100) 
          : 0;
      });

      // Convert Map to array for department satisfaction
      data.departmentSatisfaction = Array.from(departmentMap.values());

      // Calculate preference type satisfaction
      const preferenceTypeMap = new Map<string, {
        preferenceType: string;
        satisfactionScore: number;
        preferencesCount: number;
        satisfiedCount: number;
        partialCount: number;
        unsatisfiedCount: number;
      }>();

      // Initialize preference types
      const preferenceTypes = ['subject', 'year_level', 'period', 'room'];
      preferenceTypes.forEach(type => {
        preferenceTypeMap.set(type, {
          preferenceType: type,
          satisfactionScore: 0,
          preferencesCount: 0,
          satisfiedCount: 0,
          partialCount: 0,
          unsatisfiedCount: 0
        });
      });

      // Aggregate data
      data.teacherSatisfaction.forEach(teacher => {
        teacher.details.forEach(detail => {
          const type = preferenceTypeMap.get(detail.preferenceType);
          if (type) {
            type.preferencesCount++;
            if (detail.satisfied === true) {
              type.satisfiedCount++;
            } else if (detail.satisfied === 'partial') {
              type.partialCount++;
            } else {
              type.unsatisfiedCount++;
            }
          }
        });
      });

      // Calculate satisfaction scores for each preference type
      preferenceTypeMap.forEach(type => {
        type.satisfactionScore = type.preferencesCount > 0 
          ? Math.round(((type.satisfiedCount + type.partialCount * 0.5) / type.preferencesCount) * 100) 
          : 0;
      });

      // Convert Map to array for preference type satisfaction
      data.preferenceTypeSatisfaction = Array.from(preferenceTypeMap.values());

      // Calculate year level satisfaction
      const yearLevelMap = new Map<string, {
        yearLevel: string;
        satisfactionScore: number;
        preferencesCount: number;
        satisfiedCount: number;
        partialCount: number;
        unsatisfiedCount: number;
      }>();

      // Find all year levels
      const yearLevels = new Set<string>();
      assigns.forEach(a => {
        if (a.yearLevel) yearLevels.add(a.yearLevel);
      });

      // Initialize year levels
      yearLevels.forEach(level => {
        yearLevelMap.set(level, {
          yearLevel: level,
          satisfactionScore: 0,
          preferencesCount: 0,
          satisfiedCount: 0,
          partialCount: 0,
          unsatisfiedCount: 0
        });
      });

      // Aggregate data for year level preferences
      data.teacherSatisfaction.forEach(teacher => {
        teacher.details.forEach(detail => {
          if (detail.preferenceType === 'year_level') {
            // Handle ranges
            const yearLevels = detail.preferenceValue.split('-');
            yearLevels.forEach(level => {
              const yearLevel = yearLevelMap.get(level);
              if (yearLevel) {
                yearLevel.preferencesCount++;
                if (detail.satisfied === true) {
                  yearLevel.satisfiedCount++;
                } else if (detail.satisfied === 'partial') {
                  yearLevel.partialCount++;
                } else {
                  yearLevel.unsatisfiedCount++;
                }
              }
            });
          }
        });
      });

      // Calculate satisfaction scores for each year level
      yearLevelMap.forEach(level => {
        level.satisfactionScore = level.preferencesCount > 0 
          ? Math.round(((level.satisfiedCount + level.partialCount * 0.5) / level.preferencesCount) * 100) 
          : 0;
      });

      // Convert Map to array for year level satisfaction
      data.yearLevelSatisfaction = Array.from(yearLevelMap.values());

      // Calculate overall satisfaction
      const totalPreferences = data.teacherSatisfaction.reduce((sum, t) => sum + t.preferencesCount, 0);
      const totalSatisfied = data.teacherSatisfaction.reduce((sum, t) => sum + t.satisfiedCount, 0);
      const totalPartial = data.teacherSatisfaction.reduce((sum, t) => sum + t.partialCount, 0);
      data.overallSatisfaction = totalPreferences > 0 
        ? Math.round(((totalSatisfied + totalPartial * 0.5) / totalPreferences) * 100) 
        : 0;

      // Update state
      setSatisfactionData(data);
  };

  // Get filtered teacher satisfaction data
  const getFilteredTeacherSatisfaction = () => {
    if (!satisfactionData) return [];
    
    return satisfactionData.teacherSatisfaction.filter(teacher => {
      // Department filter
      if (filterDepartment !== 'all' && displayLearningArea(teacher.department) !== filterDepartment) {
        return false;
      }

      // Satisfaction filter
      if (filterSatisfaction === 'high' && teacher.satisfactionScore < 80) {
        return false;
      } else if (filterSatisfaction === 'medium' && (teacher.satisfactionScore < 50 || teacher.satisfactionScore >= 80)) {
        return false;
      } else if (filterSatisfaction === 'low' && teacher.satisfactionScore >= 50) {
        return false;
      }

      // Search query
      if (searchQuery && !teacher.teacherName.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });
  };

  // Get unique departments
  const getUniqueDepartments = () => {
    if (!satisfactionData) return [];
    const departments = new Set<string>();
    satisfactionData.teacherSatisfaction.forEach(teacher => {
      departments.add(teacher.department);
    });
    return Array.from(departments);
  };

  // Export the currently-filtered teacher satisfaction table as CSV (client-side
  // Blob download — no server round-trip). Exports exactly what is on screen.
  const handleExport = () => {
    const rows = getFilteredTeacherSatisfaction();
    if (rows.length === 0) {
      addToast('Nothing to export for the current filters', 'info');
      return;
    }

    const escape = (v: unknown) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[,"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const headers = [
      'Teacher', 'Department', 'Total Preferences',
      'Satisfied', 'Partially Satisfied', 'Unsatisfied', 'Satisfaction %'
    ];
    const lines = [
      headers.join(','),
      ...rows.map(t => [
        t.teacherName, t.department, t.preferencesCount,
        t.satisfiedCount, t.partialCount, t.unsatisfiedCount, t.satisfactionScore
      ].map(escape).join(','))
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `preference-satisfaction-${selectedYear}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    addToast(`Exported ${rows.length} teacher${rows.length === 1 ? '' : 's'} to CSV`, 'success');
  };

  // Toggle section expansion
  const toggleSection = (section: string) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  // Get satisfaction color
  const getSatisfactionColor = (score: number) => {
    if (score >= 80) {
      return 'text-ok-fg';
    } else if (score >= 50) {
      return 'text-warn-fg';
    } else {
      return 'text-danger-fg';
    }
  };

  // Get satisfaction badge
  const getSatisfactionBadge = (score: number) => {
    if (score >= 80) {
      return (
        <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-ok-bg text-ok-fg">
          High
        </span>
      );
    } else if (score >= 50) {
      return (
        <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">
          Medium
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-danger-bg text-danger-fg">
          Low
        </span>
      );
    }
  };

  // Render satisfaction stars
  const renderSatisfactionStars = (score: number) => {
    const fullStars = Math.floor(score / 20);
    const hasHalfStar = (score % 20) >= 10;

    return (
      <div className="flex">
        {[...Array(5)].map((_, i) => {
          if (i < fullStars) {
            return <Star key={i} className="h-4 w-4 text-warn-fg fill-warn-solid" />;
          } else if (i === fullStars && hasHalfStar) {
            return <StarHalf key={i} className="h-4 w-4 text-warn-fg fill-warn-solid" />;
          } else {
            return <Star key={i} className="h-4 w-4 text-ink-300" />;
          }
        })}
      </div>
    );
  };

  // Render satisfaction status icon
  const renderSatisfactionStatus = (satisfied: boolean | 'partial') => {
    if (satisfied === true) {
      return <Check className="h-5 w-5 text-ok-fg" />;
    } else if (satisfied === 'partial') {
      return <AlertTriangle className="h-5 w-5 text-warn-fg" />;
    } else {
      return <X className="h-5 w-5 text-danger-fg" />;
    }
  };

  // Render teacher details
  const renderTeacherDetails = (teacherId: string) => {
    if (!satisfactionData) return null;
    
    const teacher = satisfactionData.teacherSatisfaction.find(t => t.teacherId === teacherId);
    if (!teacher) return null;

    return (
      <Card className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-body-sm font-medium text-ink-700">
            Preference Details for {teacher.teacherName}
          </h4>
          <div className="flex items-center space-x-2">
            <div className={`text-lg font-medium font-mono tabular-nums ${getSatisfactionColor(teacher.satisfactionScore)}`}>
              {teacher.satisfactionScore}%
            </div>
            {renderSatisfactionStars(teacher.satisfactionScore)}
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Total Preferences</div>
              <div className="text-lg font-medium text-ink-900 font-mono tabular-nums">
                {teacher.preferencesCount}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Satisfied</div>
              <div className="text-lg font-medium text-ok-fg font-mono tabular-nums">
                {teacher.satisfiedCount}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Partially Satisfied</div>
              <div className="text-lg font-medium text-warn-fg font-mono tabular-nums">
                {teacher.partialCount}
              </div>
            </div>
            <div className="bg-white p-4 rounded-md border">
              <div className="text-meta text-ink-500">Unsatisfied</div>
              <div className="text-lg font-medium text-danger-fg font-mono tabular-nums">
                {teacher.unsatisfiedCount}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200">
              <thead className="bg-ink-50">
                <tr>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Preference Type
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Preference Value
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Strength
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                    Assigned Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-ink-200">
                {teacher.details.map((detail, index) => (
                  <tr 
                    key={index} 
                    className={
                      detail.satisfied === true 
                        ? 'bg-ok-bg' 
                        : detail.satisfied === 'partial' 
                        ? 'bg-warn-bg' 
                        : 'bg-danger-bg'
                    }
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-body-sm font-medium text-ink-900">
                        {detail.preferenceType.replace('_', ' ').charAt(0).toUpperCase() + 
                         detail.preferenceType.replace('_', ' ').slice(1)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-900">
                      {detail.preferenceValue}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-4 w-4 ${
                              i < detail.strength 
                                ? 'text-warn-fg fill-warn-solid' 
                                : 'text-ink-300'
                            }`} 
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        {renderSatisfactionStatus(detail.satisfied)}
                        <span className="ml-2 text-body-sm text-ink-900">
                          {detail.satisfied === true 
                            ? 'Satisfied' 
                            : detail.satisfied === 'partial' 
                            ? 'Partially Satisfied' 
                            : 'Not Satisfied'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-body-sm text-ink-900">
                      {detail.assignedValue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div>
      <PageHeader
        title="Preference Satisfaction Analysis"
        description="How well each teacher's ranked learning-area preferences match what they were actually allocated across the active grids. Higher-ranked preferences count for more."
        actions={
          <div className="flex space-x-2">
            <div className="flex items-center space-x-2">
              <label className="text-body-sm text-ink-700">Year:</label>
              <select
                className="border border-ink-300 rounded-md px-3 py-2 text-body-sm"
                value={selectedYear ?? ''}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                disabled={isLoading || availableYears.length === 0}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <Button
              variant="secondary"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => loadRealData()}
              loading={isLoading}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              leftIcon={<Download className="h-4 w-4" />}
              onClick={handleExport}
              disabled={!satisfactionData}
            >
              Export CSV
            </Button>
          </div>
        } 
      />

      {isLoading ? (
        <Card className="p-8">
          <div className="flex justify-center py-8">
            <Spinner size="lg" label={`Loading intentions and grid assignments${selectedYear ? ` for ${selectedYear}` : ''}…`} />
          </div>
        </Card>
      ) : satisfactionData ? (
        <div className="space-y-6">
          {/* Overall Satisfaction Card */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-card-title text-ink-900">
                Overall Preference Satisfaction
              </h3>
              <div className="flex items-center space-x-3">
                <div className={`text-2xl font-bold font-mono tabular-nums ${getSatisfactionColor(satisfactionData.overallSatisfaction)}`}>
                  {satisfactionData.overallSatisfaction}%
                </div>
                {getSatisfactionBadge(satisfactionData.overallSatisfaction)}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Teachers</div>
                    <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                      {satisfactionData.teacherSatisfaction.length}
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-navy-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-navy-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Total Preferences</div>
                    <div className="text-2xl font-bold text-ink-900 font-mono tabular-nums">
                      {satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.preferencesCount, 0)}
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-navy-100 rounded-full flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-navy-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Satisfied</div>
                    <div className="text-2xl font-bold text-ok-fg font-mono tabular-nums">
                      {satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.satisfiedCount, 0)}
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-ok-bg rounded-full flex items-center justify-center">
                    <Check className="h-5 w-5 text-ok-fg" />
                  </div>
                </div>
                <div className="mt-1 text-meta text-ink-500">
                  {satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.preferencesCount, 0) > 0 
                    ? Math.round((satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.satisfiedCount, 0) / 
                                  satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.preferencesCount, 0)) * 100)
                    : 0}% of preferences
                </div>
              </div>

              <div className="bg-white p-4 rounded-md border">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-body-sm text-ink-500">Unsatisfied</div>
                    <div className="text-2xl font-bold text-danger-fg font-mono tabular-nums">
                      {satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.unsatisfiedCount, 0)}
                    </div>
                  </div>
                  <div className="h-10 w-10 bg-danger-bg rounded-full flex items-center justify-center">
                    <X className="h-5 w-5 text-danger-fg" />
                  </div>
                </div>
                <div className="mt-1 text-meta text-ink-500">
                  {satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.preferencesCount, 0) > 0 
                    ? Math.round((satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.unsatisfiedCount, 0) / 
                                  satisfactionData.teacherSatisfaction.reduce((sum, t) => sum + t.preferencesCount, 0)) * 100)
                    : 0}% of preferences
                </div>
              </div>
            </div>

            <div className="mt-6 bg-navy-50 p-4 rounded-md border border-navy-200">
              <div className="flex">
                <ThumbsUp className="h-5 w-5 text-navy-500 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-body-sm font-medium text-navy-800 mb-1">
                    Satisfaction Summary
                  </h4>
                  <p className="text-body-sm text-navy-700">
                    Overall, {satisfactionData.overallSatisfaction}% of teacher preferences have been satisfied in the current timetable.
                    {satisfactionData.overallSatisfaction >= 80 
                      ? ' This is a high level of satisfaction, indicating that the timetable effectively meets most teacher preferences.' 
                      : satisfactionData.overallSatisfaction >= 50 
                      ? ' This is a moderate level of satisfaction. Consider reviewing the unsatisfied preferences to see if improvements can be made.' 
                      : ' This is a low level of satisfaction. Significant improvements may be needed to better accommodate teacher preferences.'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Department Satisfaction */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('department')}>
              <h3 className="text-card-title text-ink-900">
                Learning Area Satisfaction
              </h3>
              {expandedSection === 'department' ? (
                <ChevronUp className="h-5 w-5 text-ink-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-ink-500" />
              )}
            </div>

            {expandedSection === 'department' && satisfactionData.departmentSatisfaction.length > 0 && (
              <div className="mt-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {satisfactionData.departmentSatisfaction.map((dept, index) => (
                    <div key={index} className="bg-white p-4 rounded-md border">
                      <div className="flex justify-between items-start">
                        <h4 className="text-body-sm font-medium text-ink-900">
                          {displayLearningArea(dept.department)}
                        </h4>
                        {getSatisfactionBadge(dept.satisfactionScore)}
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className={`text-2xl font-bold font-mono tabular-nums ${getSatisfactionColor(dept.satisfactionScore)}`}>
                          {dept.satisfactionScore}%
                        </div>
                        {renderSatisfactionStars(dept.satisfactionScore)}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="text-meta text-ink-500">Teachers</div>
                          <div className="text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                            {dept.teacherCount}
                          </div>
                        </div>
                        <div>
                          <div className="text-meta text-ink-500">Preferences</div>
                          <div className="text-body-sm font-medium text-ink-900 font-mono tabular-nums">
                            {dept.preferencesCount}
                          </div>
                        </div>
                        <div>
                          <div className="text-meta text-ink-500">Satisfied</div>
                          <div className="text-body-sm font-medium text-ok-fg font-mono tabular-nums">
                            {dept.satisfiedCount}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Teacher Satisfaction */}
          <Card>
            <div className="flex justify-between items-center cursor-pointer" onClick={() => toggleSection('teacher')}>
              <h3 className="text-card-title text-ink-900">
                Teacher Satisfaction
              </h3>
              {expandedSection === 'teacher' ? (
                <ChevronUp className="h-5 w-5 text-ink-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-ink-500" />
              )}
            </div>

            {expandedSection === 'teacher' && (
              <div className="mt-6 space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  <div className="flex-1 flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                    <div className="w-full md:w-64">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-5 w-5 text-ink-400" />
                        </div>
                        <input 
                          type="text" 
                          className="block w-full pl-10 pr-3 py-2 border border-ink-300 rounded-md leading-5 bg-white placeholder-ink-500 focus:outline-none focus:placeholder-ink-400 focus:ring-1 focus:ring-navy-500 focus:border-navy-500 sm:text-sm" 
                          placeholder="Search teachers" 
                          value={searchQuery} 
                          onChange={(e) => setSearchQuery(e.target.value)} 
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <select 
                        className="block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md" 
                        value={filterDepartment} 
                        onChange={(e) => setFilterDepartment(e.target.value)}
                      >
                        <option value="all">All Learning Areas</option>
                        {learningAreaNames.map(name => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                      <select 
                        className="block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md" 
                        value={filterSatisfaction} 
                        onChange={(e) => setFilterSatisfaction(e.target.value)}
                      >
                        <option value="all">All Satisfaction Levels</option>
                        <option value="high">High (80%+)</option>
                        <option value="medium">Medium (50-79%)</option>
                        <option value="low">Low (0-49%)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-ink-200">
                    <thead className="bg-ink-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Teacher
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Learning Area
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Preferences
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Satisfied
                        </th>
                        <th className="px-6 py-3 text-left text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Satisfaction
                        </th>
                        <th className="px-6 py-3 text-right text-meta font-medium text-ink-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-ink-200">
                      {getFilteredTeacherSatisfaction().length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-center text-body-sm text-ink-500">
                            No teachers found matching the filters
                          </td>
                        </tr>
                      ) : (
                        getFilteredTeacherSatisfaction().map(teacher => (
                          <tr key={teacher.teacherId} className="hover:bg-ink-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-body-sm font-medium text-ink-900">
                                {teacher.teacherName}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500">
                              {displayLearningArea(teacher.department)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-body-sm text-ink-500 font-mono tabular-nums">
                              {teacher.preferencesCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-body-sm text-ink-900">
                                {teacher.satisfiedCount} full / {teacher.partialCount} partial
                              </div>
                              <div className="text-meta text-ink-500">
                                {teacher.unsatisfiedCount} unsatisfied
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`text-body-sm font-medium font-mono tabular-nums ${getSatisfactionColor(teacher.satisfactionScore)} mr-2`}>
                                  {teacher.satisfactionScore}%
                                </div>
                                {renderSatisfactionStars(teacher.satisfactionScore)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-body-sm font-medium">
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => setSelectedTeacher(teacher.teacherId === selectedTeacher ? null : teacher.teacherId)}
                              >
                                {teacher.teacherId === selectedTeacher ? 'Hide Details' : 'View Details'}
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {selectedTeacher && renderTeacherDetails(selectedTeacher)}
              </div>
            )}
          </Card>
        </div>
      ) : availableYears.length === 0 ? (
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-warn-fg mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">
            No teaching intentions yet
          </h3>
          <p className="text-ink-500 mb-4 max-w-lg mx-auto">
            There are no submitted teaching intentions in the system, so there is nothing to
            measure preference satisfaction against. Once teachers submit their intention forms,
            this report will compare what they asked for against what they were allocated.
          </p>
        </Card>
      ) : (
        <Card className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-warn-fg mx-auto mb-4" />
          <h3 className="text-card-title text-ink-900 mb-2">
            No ranked preferences for {selectedYear}
          </h3>
          <p className="text-ink-500 mb-4 max-w-lg mx-auto">
            Intentions exist for {selectedYear}, but none of them list a ranked curriculum-area
            preference to score. This normally means the {selectedYear} forms were imported without
            preference rankings. Pick another year above, or check that the intention forms capture
            preferred learning areas.
          </p>
          <Button
            variant="primary"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={() => loadRealData()}
          >
            Retry
          </Button>
        </Card>
      )}
    </div>
  );
};

export default PreferenceSatisfaction;
